/**
 * **What a session did — or will do — to its client's credits**, and where the client stands: the
 * panel's "Żetony" and "Saldo" lines (T-A5), shown only while the trainer runs credits (R18).
 *
 * The session's line is read off R10's own table (`creditConsumption`, pinned by the backend's
 * fixture) and nothing else: WHEN a credit goes depends on the origin — at creation for a booking,
 * at the outcome for a plan or series session — and whether it has gone is `consumesCredit`. A
 * forgiveness (R14) found in the client's ledger overrides it. The client's standing is the
 * server's own figure (`GET /credit-ledger/summary`): a negative balance is said as sessions to
 * settle, never as a debt (R16).
 *
 * Pure: no React, no DOM, no query hook, no i18next. Words are i18n KEYS.
 */

import type { BookingSession } from './bookingSession';
import { CONSUMED_AT_CREATION, CONSUMED_AT_OUTCOME, consumesCredit } from './creditConsumption';
import { isChargeForgiven } from './sessionActions';
import { creditStanding, type CreditStanding } from '~/features/credit-ledger';

export type SessionCreditFact =
  /** A booking's credit, taken when it was made. */
  | 'reserved'
  /** A plan or series session's credit, taken when it was held. */
  | 'held'
  /** A plan or series session still ahead: its credit goes only when it is held. */
  | 'onOutcome'
  /** A plan or series session that did not happen and was not charged — missed or declined. */
  | 'notTaken'
  /** Called off in time: the booking's credit came back, or a plan session never took one. */
  | 'returned'
  | 'lateCancelled'
  | 'noShow'
  /** Taken back by the trainer (R14). */
  | 'forgiven'
  /** Outside the ledger: a booking-link session paid on site, or one with no trainer. */
  | 'outside';

export function sessionCreditFact(session: BookingSession, forgiven: boolean): SessionCreditFact {
  const status = session.wireStatus;
  const origin = session.origin;
  const consumes = consumesCredit({ origin, status, carriesTrainer: session.carriesTrainer });
  if (forgiven && consumes) return 'forgiven';
  const creation = origin === null || CONSUMED_AT_CREATION.has(origin);
  if (!session.carriesTrainer || (!creation && !CONSUMED_AT_OUTCOME.has(origin))) return 'outside';
  if (status === 'LateCancelled') return 'lateCancelled';
  if (status === 'NoShow') return 'noShow';
  if (!consumes) {
    if (creation || status === 'Cancelled') return 'returned';
    // Only a session still ahead (or running) may yet cost its credit; a missed or declined one
    // was not charged, and saying "it will be" would promise a charge R10 never takes.
    const ahead =
      session.status === 'scheduled' || session.status === 'live' || session.status === 'emptySlot';
    return ahead ? 'onOutcome' : 'notTaken';
  }
  return creation ? 'reserved' : 'held';
}

/**
 * Whether the trainer's call-off gives this session's credit back: it costs one now, and a
 * `Cancelled` session would not (R10). A trainer's cancel is never late (R14, calendar-management
 * R7), so this is the whole question — a plan or series session never took one to give back.
 */
export function cancelGivesCreditBack(session: BookingSession): boolean {
  const input = { origin: session.origin, carriesTrainer: session.carriesTrainer };
  return (
    consumesCredit({ ...input, status: session.wireStatus }) &&
    !consumesCredit({ ...input, status: 'Cancelled' })
  );
}

/** A read as the panel waits on it — the three states, never a branch on the data. */
export type ReadState<T> =
  | { readonly kind: 'pending' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'ready'; readonly data: T };

/**
 * A query's answer as a `ReadState`. Data in hand wins over a failed refetch: what was read stands.
 * A read that has failed is `failed` while it is read AGAIN — TanStack Query reports a refetch of a
 * query with no data as `pending`, which would take the failure (and its retry) off the screen
 * for the length of the retry.
 */
export function readState<T>(read: {
  readonly data: T | undefined;
  readonly isError: boolean;
  readonly errorUpdateCount: number;
}): ReadState<T> {
  if (read.data !== undefined) return { kind: 'ready', data: read.data };
  return read.isError || read.errorUpdateCount > 0 ? { kind: 'failed' } : { kind: 'pending' };
}

interface Summary {
  readonly creditsEnabled: boolean;
  readonly clients?:
    | readonly {
        readonly customerId: number;
        readonly balance: number;
        readonly sessionsOwed: number;
      }[]
    | null;
}

/**
 * Where one client stands in the trainer's credit summary — the one reading of it, for the session
 * panel and the new session's client field alike. `standing: null` is a client the summary does
 * not list (not on the active roster).
 */
export type ClientStanding =
  | { readonly kind: 'pending' }
  /** The summary did not answer — never read as credits off. */
  | { readonly kind: 'failed' }
  /** The trainer does not run credits (R18). */
  | { readonly kind: 'off' }
  | { readonly kind: 'standing'; readonly standing: CreditStanding | null };

export function clientStanding(summary: ReadState<Summary>, customerId: number): ClientStanding {
  if (summary.kind !== 'ready') return summary;
  if (!summary.data.creditsEnabled) return { kind: 'off' };
  const row = summary.data.clients?.find((entry) => entry.customerId === customerId);
  return { kind: 'standing', standing: row === undefined ? null : creditStanding(row) };
}

/**
 * Whether the trainer may be offered to take this session's charge back, as far as is known —
 * `pending` while the client's ledger is still being read.
 */
export type Forgiveness = 'offer' | 'forgiven' | 'unknown' | 'notApplicable' | 'pending';

/** What the panel draws about credits. */
export type CreditPanel =
  /** The summary has not answered: the lines stand as bars. */
  | { readonly kind: 'pending' }
  /** The trainer does not run credits (R18): nothing about them is drawn. */
  | { readonly kind: 'off' }
  /** The summary did not answer: a quiet line with a retry — never read as credits off. */
  | { readonly kind: 'failed' }
  | {
      readonly kind: 'ready';
      /** `null` while the ledger is read: whether the charge was taken back decides the words. */
      readonly fact: SessionCreditFact | null;
      readonly standing: CreditStanding | null;
      readonly forgiveness: Forgiveness;
      /** A late cancellation's credit is gone, and the panel says what that means. */
      readonly lateCancelNote: boolean;
    };

interface Ledger {
  readonly movements?:
    readonly { readonly kind: string | null; readonly calendarEventId?: number | null }[] | null;
}

/**
 * The one reading of the panel's credit reads. The ledger is asked only when the charge could be
 * taken back (`forgivable`); if it fails the row is NOT offered blindly — `unknown`, said as such.
 * Everything that decides the panel's SHAPE is known without the ledger: the standing line and the
 * late-cancel note stand at once, and only the credit's words and the forgiveness row wait.
 */
export function creditPanel(input: {
  readonly session: BookingSession;
  readonly standing: ClientStanding;
  readonly forgivable: boolean;
  readonly ledger: ReadState<Ledger>;
}): CreditPanel {
  const { session, standing, forgivable, ledger } = input;
  if (standing.kind !== 'standing') return standing;
  const forgiven =
    ledger.kind === 'ready' && isChargeForgiven(ledger.data.movements ?? [], session.id);
  const forgiveness: Forgiveness = !forgivable
    ? 'notApplicable'
    : ledger.kind === 'pending'
      ? 'pending'
      : ledger.kind === 'failed'
        ? 'unknown'
        : forgiven
          ? 'forgiven'
          : 'offer';
  return {
    kind: 'ready',
    fact: forgiveness === 'pending' ? null : sessionCreditFact(session, forgiven),
    standing: standing.standing,
    forgiveness,
    lateCancelNote:
      sessionCreditFact(session, false) === 'lateCancelled' && forgiveness !== 'forgiven',
  };
}
